import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { useLogin } from "@/features/auth/hooks/use-login";
import { TwoStepAuthCodeModal } from "@/features/two-step-authentication/components/two-step-authentication-code-modal";
import { TwoStepReleaseCodeModal } from "@/features/two-step-authentication/components/two-step-authentication-release-code-modal";
import { MailAddressForm } from "@/shared/components/text-box/mail-address-form";
import { PasswordForm } from "@/shared/components/text-box/password-form";
import { EndpointPathNames } from "@/shared/config/endpoint-path";
import { LoginHeader } from "@/shared/components/header/login-header";
import { Footer } from "@/shared/components/footer/footer";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { useLoginContext } from "@/features/auth/contexts/login-context";
import { NotConvertedModal } from "@/features/auth/components/not-converted-modal";
import { ConfirmNotConvertModal } from "@/features/auth/components/confirm-not-convert-modal";

export const Login = () => {
  const {
    openNotLoginModal,
    setOpenNotLoginModal,
  } = useModalContext();
  const {
    email,
    setEmail,
    setTwoStepAuthCode,
    setTwoStepReleaseCode,
    setAdditionalParams,
  } = useLoginContext();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    password,
    errorMessage,
    isActiveLoginButton,
    onChangeMailAddress,
    onChangePassword,
    onClickLogin,
    changeLoginButtonState,
    callbackCodeModal,
  } = useLogin();
  const [loginNoticeMessage, setLoginNoticeMessage] = useState<string>("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    const reloginCheck = () => {
      // 仕訳ジャンプエラーメッセージ定義
      const jumpErrorMessages = {
        jump_session_expired:
          "指定明細を表示するための有効期限が切れました。\n連携元から再度操作してください。",
        jump_target_owner_error:
          "仕訳元Web明細帳所有者が削除されたため明細を表示できません",
        jump_target_passbook_error:
          "口座が存在しないため明細を表示できません",
        jump_system_error:
          "指定明細の表示中にエラーが発生しました。\nしばらくおまちいただいた後に再度お試しください。",
      } as const;
      type JumpErrorKey = keyof typeof jumpErrorMessages;
      const isJumpErrorKey = (value: string): value is JumpErrorKey =>
        Object.prototype.hasOwnProperty.call(jumpErrorMessages, value);
 
      const queryParams = new URLSearchParams(location.search);
      const jumpError = queryParams.get("jump_error");
      const sessionInvalid = queryParams.get("session_invalid");
      const maintenance = queryParams.get("maintenance");
      const jumpType = queryParams.get("jump");
      const passbookSid = queryParams.get("passbooksid");
      const detailSid = queryParams.get("detailsid");
      const tableType = queryParams.get("tabletype");
      const ownerId = queryParams.get("owner_id");
      if (maintenance === "1") {
        // メンテナンスモードの場合のメッセージ表示
        setLoginNoticeMessage("現在、システムメンテナンス中のため、ログインできません。ご迷惑をおかけしますが、しばらく経ってから再度ログインしてください。");
      }
      if (jumpError !== null) {
        // 仕訳ジャンプ無効の場合のメッセージ表示
        const message =
          isJumpErrorKey(jumpError)
            ? jumpErrorMessages[jumpError]
            : jumpErrorMessages.jump_system_error;
        setLoginNoticeMessage(message);
      } else if (sessionInvalid === "true") {
        // セッション無効の場合のメッセージ表示
        setLoginNoticeMessage("ログイン状態の期限が切れたため、再度ログインしてください。");
      }
      if (jumpType !== null && passbookSid !== null && detailSid !== null && tableType !== null) {
        setAdditionalParams(`?jump=${jumpType}&passbooksid=${passbookSid}&detailsid=${detailSid}&tabletype=${tableType}`);
      }
      if (ownerId !== null) {
        const decodedOwnerId = decodeURIComponent(ownerId);
        setEmail(decodedOwnerId);
      }
      // エンドポイントから口座情報(account_group_idなど)を消す
      navigate("/login", { replace: true });
    };
    reloginCheck();
    setShow(true);
  }, [location.search]);

  // 新規登録遷移
  const onClickSignUp = () => {
    navigate(EndpointPathNames.SIGN_UP);
  };

  useEffect(() => {
    changeLoginButtonState(email, password);
  }, [email, password]);

  // メールアドレスフォームに付与するID
  const mailAddressFormId = "loginId";
  // パスワードフォームに付与するID(メールアドレスの入力後、次にフォーカスを当てたい要素)
  const passwordFormId = "loginPass";
  // ログインボタンに付与するID(パスワードの入力後、次にフォーカスを当てたい要素)
  const loginButtonId = "btnLogin";
  // 新規利用者登録ボタンに付与するID
  const signUpButtonId = "sign_up_button_id";
  // パスワードを忘れた方はこちらボタンに付与するID
  // const resetPassButtonId = "reset_pass_button_id";

  if (!show) {
    return null;
  }
  return (
    <>
      <LoginHeader
        content = ""
        className="login"
      />
      <main>
        <section id="loginBox">
          {/* ログイン誘導メッセージ */}
          {loginNoticeMessage && (
            <div className="login__session_invalid_message">{loginNoticeMessage}</div>
          )}
          <ul>
            <li>
              <MailAddressForm
                className="loginId"
                onChange={onChangeMailAddress}
                id={mailAddressFormId}
                title="メールアドレス"
                placeholder={"メールアドレスを入力"}
                initialValue={email}
                autoComplete="username"
              />
            </li>
            <li>
              <PasswordForm
                className="loginPass"
                onChange={onChangePassword}
                id={passwordFormId}
                title="パスワード"
                placeholder={"パスワードを入力（半角英数8文字以上）"}
                initialValue={password}
                autoComplete="current-password"
              />
            </li>
          </ul>
          <div className="login__error_message">{errorMessage}</div>
          <nav>
            <BaseRoundButton
              className="nav-main"
              id={loginButtonId}
              buttonText="ログイン"
              onClick={(e) => onClickLogin(email, password, e)}
              isActive={isActiveLoginButton}
            />
          </nav>
        </section>
        <div className="float">
          <p>
            <BaseRoundButton
              id={signUpButtonId}
              buttonText="はじめてご利用の方"
              onClick={() => onClickSignUp()}
            />
          </p>
        {/* 「パスワードを忘れた方はこちら」ボタン */}
          <Link
            to={EndpointPathNames.RESET_PASS}>
            パスワードをお忘れの方
          </Link>
        </div>
      </main>
      <Footer />
      {/* 2段階認証モーダル */}
      {openNotLoginModal === "TwoStepAuthCode" && (
      <TwoStepAuthCodeModal
        onCloseModal={() => {
          setTwoStepAuthCode("");
          callbackCodeModal({ status: "cancel" });
        }}
        onCompleteModal={(result) => {
          setTwoStepAuthCode("");
          callbackCodeModal(result);
        }}
      />
      )}

      {/* 2段階認証解除モーダル */}
      {openNotLoginModal === "TwoStepAuthReleaseCode" && (
      <TwoStepReleaseCodeModal
        onCloseModal={() => {
          setTwoStepReleaseCode("");
          callbackCodeModal({ status: "cancel" });
        }}
        onCompleteModal={(result) => {
          setTwoStepReleaseCode("");
          callbackCodeModal(result);
        }}
      />
      )}

      {openNotLoginModal === "NotConverted" && (
      <NotConvertedModal
        onTapCloseModal={() => setOpenNotLoginModal(undefined)}
      />
      )}
  
      {openNotLoginModal === "ConfirmNotConvert" && (
      <ConfirmNotConvertModal
        onTapCloseModal={() => setOpenNotLoginModal(undefined)}
      />
      )}
    </>
  );
};
